import React from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

/** Shared footprint so unsorted / ASC / DESC never shift header layout. */
export const TABLE_SORT_ICON_SX = {
  flex: "0 0 auto",
  fontSize: "14px !important",
  width: 14,
  height: 14,
  color: "text.secondary",
};

/**
 * Normalize RDG (`ASC`/`DESC`) and MUI (`asc`/`desc`) sort directions.
 * @param {string|null|undefined} sortDirection
 * @returns {'ASC' | 'DESC' | null}
 */
export function normalizeTableSortDirection(sortDirection) {
  if (!sortDirection) return null;
  const value = String(sortDirection).toUpperCase();
  if (value === "ASC" || value === "DESC") return value;
  return null;
}

/**
 * Always-visible sort affordance for spreadsheet / company tables.
 * Neutral ↕ when unsorted; ↑ / ↓ when active. Same size in every state.
 */
export function TableSortIndicator({ sortDirection, testId = "table-sort-indicator" }) {
  const direction = normalizeTableSortDirection(sortDirection);
  const Icon =
    direction === "ASC"
      ? ArrowUpwardIcon
      : direction === "DESC"
        ? ArrowDownwardIcon
        : UnfoldMoreIcon;

  return (
    <Icon
      data-testid={testId}
      data-sort-direction={direction ? direction.toLowerCase() : "none"}
      aria-hidden="true"
      sx={{
        ...TABLE_SORT_ICON_SX,
        opacity: direction ? 0.85 : 0.45,
      }}
    />
  );
}

/** MUI DataGrid unsorted glyph (always shown beside the label). */
export function TableColumnUnsortedIcon(props) {
  return (
    <UnfoldMoreIcon
      {...props}
      data-testid="table-sort-indicator"
      data-sort-direction="none"
      sx={{ fontSize: 16, ...(props.sx || {}) }}
    />
  );
}

/**
 * MUI DataGrid styles: keep sort icons always visible beside the column label.
 */
export function getAlwaysVisibleSortIconSx(theme) {
  const isDark = theme?.palette?.mode === "dark";
  const muted = isDark ? "rgba(255,255,255,0.45)" : "rgba(88,89,91,0.55)";
  const active = isDark ? "rgba(255,255,255,0.78)" : "rgba(88,89,91,0.9)";

  return {
    // Sit the icon immediately after the label (not flush-right).
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      justifyContent: "flex-start",
      gap: "4px",
      width: "auto",
      maxWidth: "100%",
    },
    "& .MuiDataGrid-columnHeaderTitleContainerContent": {
      flex: "0 1 auto",
      minWidth: 0,
      overflow: "hidden",
    },
    "& .MuiDataGrid-iconButtonContainer": {
      visibility: "visible !important",
      width: "auto !important",
      marginLeft: 0,
      flex: "0 0 auto",
    },
    "& .MuiDataGrid-sortIcon": {
      opacity: "0.45 !important",
      color: muted,
      fontSize: 16,
    },
    "& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon": {
      opacity: "0.85 !important",
      color: active,
    },
  };
}

/** @deprecated Use getAlwaysVisibleSortIconSx */
export const getSortedOnlySortIconSx = getAlwaysVisibleSortIconSx;
